import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { auth, notifications, messageErreur } from '../services/api';
import { Avatar, useFermeture } from './communs';
import { ilYA, nomComplet } from '../utils/format';

const NAV = [
  { to: '/', libelle: 'Tableau de bord', exact: true },
  { to: '/projets', libelle: 'Projets' },
  { to: '/mes-taches', libelle: 'Mes tâches' },
  { to: '/documentation', libelle: 'Documentation' },
];

// ─── La cloche : notifications dans l'application, relevées toutes les 30 s ──
function Cloche() {
  const [donnees, setDonnees] = useState({ notifications: [], non_lues: 0 });
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const ref = useFermeture(ouvert, setOuvert);
  const naviguer = useNavigate();

  const charger = useCallback(() => notifications.liste()
    .then((d) => { setDonnees(d); setErreur(null); })
    .catch((e) => setErreur(messageErreur(e))), []);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 30000);
    window.addEventListener('focus', charger);
    return () => { clearInterval(t); window.removeEventListener('focus', charger); };
  }, [charger]);

  async function ouvrir(n) {
    setOuvert(false);
    if (!n.lue_le) notifications.lue(n.id).then(charger).catch(() => {});
    if (n.projet_id) naviguer(`/projets/${n.projet_id}${n.tache_id ? `?tache=${n.tache_id}` : ''}`);
  }

  async function toutLu() {
    try { await notifications.toutLu(); charger(); } catch (e) { setErreur(messageErreur(e)); }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="bouton-icone"
        onClick={() => { setOuvert((o) => !o); if (!ouvert) charger(); }}
        aria-label={`Notifications${donnees.non_lues ? ` (${donnees.non_lues} non lues)` : ''}`}
        aria-expanded={ouvert}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {donnees.non_lues > 0 && <span className="pastille">{donnees.non_lues > 99 ? '99+' : donnees.non_lues}</span>}
      </button>
      {ouvert && (
        <div className="menu-deroulant" role="dialog" aria-label="Notifications">
          <div className="menu-entete">
            <span>Notifications</span>
            {donnees.non_lues > 0 && <button type="button" className="btn btn-petit" onClick={toutLu}>Tout marquer comme lu</button>}
          </div>
          {erreur && <div className="erreur-bandeau" style={{ margin: 12 }}>{erreur}</div>}
          <div className="menu-liste">
            {donnees.notifications.length === 0 && !erreur && (
              <div className="vide" style={{ padding: 24 }}>Aucune notification. Vous serez prévenu ici quand on vous assigne une tâche ou qu’on vous mentionne.</div>
            )}
            {donnees.notifications.map((n) => (
              <button key={n.id} type="button" className={`notif ${n.lue_le ? '' : 'non-lue'}`} onClick={() => ouvrir(n)}>
                <span className="notif-point" aria-hidden="true" />
                <span>
                  <div className="notif-texte">{n.texte}</div>
                  <div className="notif-quand">{ilYA(n.created_at)}</div>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Menu du profil : préférence e-mail, déconnexion ─────────────────────────
function MenuProfil({ user, setUser, onDeconnexion }) {
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState(null);
  const ref = useFermeture(ouvert, setOuvert);

  async function basculerEmail(e) {
    try {
      const r = await auth.preferences({ notif_email: e.target.checked });
      setUser(r.user); setErreur(null);
    } catch (err) {
      setErreur(messageErreur(err));
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="bouton-profil" onClick={() => setOuvert((o) => !o)} aria-label="Mon profil" aria-expanded={ouvert}>
        <Avatar prenom={user.prenom || user.email} nom={user.nom} />
      </button>
      {ouvert && (
        <div className="menu-deroulant" style={{ width: 280 }}>
          <div className="menu-profil">
            <div className="qui">{nomComplet(user) || user.email}</div>
            <div className="role">{user.role === 'admin' ? 'Administrateur — voit tout le groupe' : 'Membre — voit ses projets et ses tâches'}</div>
            {erreur && <div className="erreur-bandeau">{erreur}</div>}
            <label className="bascule">
              <input type="checkbox" checked={user.notif_email !== false} onChange={basculerEmail} />
              Recevoir les notifications par e-mail
            </label>
            <button type="button" className="btn" style={{ width: '100%', justifyContent: 'center' }} onClick={onDeconnexion}>Se déconnecter</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Bandeau supérieur : même gabarit qu'A97 Hub, logotype A97 + « Projets ».
export default function EnTete({ user, setUser, onDeconnexion }) {
  return (
    <header className="app-header">
      <Link to="/" className="app-marque" aria-label="A97 Projets — tableau de bord">
        <span className="a97">A<span>97</span></span><span className="produit">Projets</span>
      </Link>
      <nav className="app-nav">
        {NAV.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.exact} className={({ isActive }) => (isActive ? 'actif' : '')}>{l.libelle}</NavLink>
        ))}
        {user.role === 'admin' && (
          <NavLink to="/parametres" style={{ marginLeft: 'auto' }} className={({ isActive }) => (isActive ? 'actif' : '')}>Paramètres</NavLink>
        )}
      </nav>
      <div className="entete-droite">
        <Cloche />
        <MenuProfil user={user} setUser={setUser} onDeconnexion={onDeconnexion} />
      </div>
    </header>
  );
}
